// About.jsx
import React, { useEffect, useRef, useState } from 'react';
import { 
  Award, Users, Coffee, Clock, Shield, Wifi, Star, Heart, ChevronRight, 
  MapPin, Phone, Mail, Calendar, Sparkles, Building2, ParkingCircle,
  Utensils, Wind, Tv, Dumbbell, Bath, Car, Volume2, Sun
} from 'lucide-react';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    { icon: Award, title: 'Luxury Rooms', description: 'Elegantly designed rooms with premium amenities and stunning city views' },
    { icon: Users, title: 'Family Friendly', description: 'Perfect for family vacations with spacious rooms and kid-friendly services' },
    { icon: Coffee, title: 'Complimentary Breakfast', description: 'Start your day with delicious continental breakfast buffet' },
    { icon: Clock, title: '24/7 Room Service', description: 'Round-the-clock room service and guest support' },
    { icon: Shield, title: 'Safe & Secure', description: '24/7 CCTV surveillance and secure premises with trained staff' },
    { icon: Wifi, title: 'High-Speed WiFi', description: 'Free high-speed internet connectivity throughout the property' },
  ];

  const amenities = [
    { icon: Wind, name: 'Air Conditioning', available: true },
    { icon: Tv, name: 'Smart LED TV', available: true },
    { icon: Bath, name: 'Modern Bathroom', available: true },
    { icon: Coffee, name: 'Tea/Coffee Maker', available: true },
    { icon: Wifi, name: 'Free WiFi', available: true },
    { icon: ParkingCircle, name: 'Free Parking', available: true },
    { icon: Utensils, name: 'Restaurant', available: true },
    { icon: Dumbbell, name: 'Fitness Center', available: true },
    { icon: Car, name: 'Airport Transfer', available: true },
    { icon: Volume2, name: 'Soundproof Rooms', available: true },
  ];

  const stats = [
    { value: '500+', label: 'Happy Guests', icon: Star, color: 'from-yellow-500 to-yellow-400' },
    { value: '4.9', label: 'Rating', icon: Star, color: 'from-yellow-500 to-yellow-400' },
    { value: '50+', label: 'Luxury Rooms', icon: Building2, color: 'from-blue-500 to-blue-400' },
    { value: '6+', label: 'Years of Excellence', icon: Award, color: 'from-purple-500 to-purple-400' },
  ];

  const whyChooseUs = [
    {
      title: 'Prime Location',
      description: 'Located in the heart of the city with easy access to major attractions',
      icon: MapPin
    },
    {
      title: 'Exceptional Service',
      description: 'Dedicated staff ensuring memorable and comfortable stay',
      icon: Users
    },
    {
      title: 'Modern Amenities',
      description: 'State-of-the-art facilities for business and leisure travelers',
      icon: Sparkles
    },
    {
      title: 'Value for Money',
      description: 'Competitive pricing with luxury experience',
      icon: Heart
    }
  ];

  return (
    <section id="about" ref={sectionRef} className="py-20 md:py-28 bg-gradient-to-br from-white via-blue-50 to-indigo-50 overflow-hidden relative">
      
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse delay-1000"></div>
      
      <div className="container mx-auto px-4 md:px-8 lg:px-12 relative z-10">
        
        {/* Section Header with Creative Design */}
        <div className="text-center mb-12 md:mb-16">
          <div className={`inline-block mb-3 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="text-blue-600 font-semibold tracking-wider text-sm uppercase bg-gradient-to-r from-blue-100 to-indigo-100 px-5 py-2 rounded-full shadow-sm">
              ✦ Welcome to Paridhi ✦
            </span>
          </div>
          <h2 className={`text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            Where Luxury Meets
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-500 bg-clip-text text-transparent block md:inline md:ml-2">
              Comfort & Elegance
            </span>
          </h2>
          <div className={`w-24 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500 mx-auto rounded-full transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}></div>
          <p className={`text-gray-600 max-w-2xl mx-auto mt-5 text-base md:text-lg leading-relaxed transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            Experience unparalleled hospitality at Paridhi Guest House, where every guest is treated like family
          </p>
        </div>

        {/* Hero Story Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Left side - Image Gallery */}
          <div className={`relative transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="image6.png"
                alt="Paridhi Guest House Luxury Room"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            </div>
            
            {/* Small Image Badge */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-3 max-w-[180px] backdrop-blur-sm bg-white/95">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <Sun className="text-white" size={18} />
                </div>
              </div>
            </div>

            {/* Rating Badge */}
            <div className="absolute -top-4 -right-4 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-xl shadow-xl p-3 animate-bounce-slow">
              <div className="flex items-center gap-2">
                <Star className="text-white" fill="white" size={20} />
                <span className="text-white font-bold text-lg">4.9</span>
              </div>
            </div>
          </div>

          {/* Right side - About Content */}
          <div className={`space-y-6 transition-all duration-700 delay-600 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-800 relative inline-block">
              Our Story
              <div className="absolute -bottom-2 left-0 w-12 h-1 bg-blue-500 rounded-full"></div>
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Paridhi Guest House was founded with a vision to provide travelers with a home away from home. 
              Nestled in the heart of the city, our guest house combines traditional warmth with contemporary luxury.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Each of our rooms is thoughtfully designed to offer the perfect blend of comfort and style. 
              From premium bedding to modern amenities, every detail is curated to ensure an unforgettable stay.
            </p>
            
            {/* Quick Info Cards */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-md hover:shadow-lg transition-all group">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <MapPin size={18} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Prime Location</p>
                  <p className="text-xs text-gray-500">City Center</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-md hover:shadow-lg transition-all group">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Calendar size={18} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Check-in/out</p>
                  <p className="text-xs text-gray-500">12 PM / 11 AM</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 pt-2">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Book Your Stay
                <ChevronRight size={18} />
              </a>
              <a
                href="#gallery"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#gallery')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-3 rounded-lg font-semibold transition-all duration-300"
              >
                View Gallery
              </a>
            </div>
          </div>
        </div>

        {/* Why Choose Us Section */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <h3 className={`text-2xl md:text-3xl font-bold text-gray-800 transition-all duration-700 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              Why Choose <span className="text-blue-600">Paridhi Guest House?</span>
            </h3>
            <p className="text-gray-500 mt-2">Experience the difference with our exceptional services</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <div
                key={item.title}
                className={`bg-white rounded-xl p-6 shadow-md hover:shadow-2xl transition-all duration-300 group hover:-translate-y-2 text-center ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${700 + index * 100}ms` }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:rotate-6 transition-transform duration-300 shadow-lg">
                  <item.icon className="text-white" size={28} />
                </div>
                <h4 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h4>
                <p className="text-gray-500 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Features Grid */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <h3 className={`text-2xl md:text-3xl font-bold text-gray-800 transition-all duration-700 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              Premium <span className="text-blue-600">Amenities & Services</span>
            </h3>
            <p className="text-gray-500 mt-2">Everything you need for a perfect stay</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${800 + index * 100}ms` }}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md">
                  <feature.icon className="text-white" size={28} />
                </div>
                <h4 className="text-lg font-semibold text-gray-800 mb-2">{feature.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
        {/* Contact Info Banner */}
        <div className={`bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600 rounded-2xl p-8 md:p-10 text-center transition-all duration-700 delay-1100 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Ready to Experience Luxury?
          </h3>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            Book your stay today and create unforgettable memories at Paridhi Guest House
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <Phone size={18} className="text-white" />
              <span className="text-white">+91 95550 40589</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <Mail size={18} className="text-white" />
              <span className="text-white">paridhiguest@gmail.com</span>
            </div>
          </div>
          <div className="mt-6">
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Book Now
              <ChevronRight size={18} />
            </a>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.2;
          }
          50% {
            opacity: 0.3;
          }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        .animate-pulse {
          animation: pulse-slow 4s ease-in-out infinite;
        }
        .delay-1000 {
          animation-delay: 1000ms;
        }
      `}</style>
    </section>
  );
};

export default About;